import { EnumSchema } from '@40seas/entities/common';
import { z } from 'zod';
import { HeronReportCreditScoreStatus } from './heron-report.credit-score';
import { Currency } from '@40seas/models';

export enum HeronDataUploadStatus {
    Succeeded = 'succeeded',
    Failed = 'failed',
}

export const HeronReportFinancialConnectionUploadSchema = z.object({
    financialConnection: z.string(),
    lastTransactionAt: z.date().nullish(),
    lastBalanceAt: z.date().nullish(),
    status: EnumSchema(HeronDataUploadStatus),
});

export class HeronReportFinancialConnectionUpload {
    financialConnection: string;
    firstTransactionDate?: Date;
    lastTransactionDate?: Date;
    totalTransactions?: number;
    lastBalanceDate?: Date;
    status: HeronDataUploadStatus;
}

// https://docs.herondata.io/api#tag/EndUserCalculations/paths/~1api~1end_users~1%7Bend_user_id_or_heron_id%7D~1export_spreadsheet/get
export class HeronScoreResponse {
    data_quality_issues: HeronScoreQualityIssues[];
    predicted_at: string; //dateTime
    reference_timestamp: string; // datetime
    status: HeronReportCreditScoreStatus;
    value: number;
}

export type HeronScoreQualityIssues =
    | 'transactions_without_timestamps'
    | 'date_range_too_low'
    | 'confidence_too_low'
    | 'ctg_coverage_too_low'
    | 'data_volume_too_low'
    | 'data_not_fresh';

export class HeronStatisticsResponse {
    statistics: {
        currency: Currency;
        dt: string; // "yyyy-mm-dd"
        heron_id: string;
        label: string; //"Revenue" | "Payroll and Consultants"
        metrics: {
            inflow: {
                amount: number;
                avg_amount: number;
                n: number;
            };
            net: {
                amount: number;
                avg_amount: number;
                n: number;
            };
            outflow: {
                amount: number;
                avg_amount: number;
                n: number;
            };
        };
    }[];
}

export class HeronBankStatementSummaryResponse {
    by_month: {
        [key: string]: object;
    };
    total: {
        negative_balance_days: number;
    };
}

export class HeronProfitAndLoss {
    dates: string[]; // "yyyy-mm-dd for example 2024-05-01",
    sections: {
        metrics: {
            confidence: number;
            indent: number;
            is_ratio: boolean;
            name: HeronProfitAndLossTypes;
            values: number[]; // compatible with dates array
        }[];
        name: HeronSectionName;
    }[];
}

export type HeronSectionName =
    | 'Special Items'
    | 'Other Income'
    | 'Cash from financing activities'
    | 'Tax Expenses'
    | 'Operational Expenses'
    | 'Cost of Goods Sold'
    | 'Revenue';
export type HeronProfitAndLossTypes =
    | 'Revenue'
    | 'Check Deposits'
    | 'ATM Cash Inflows'
    | 'Total Revenue'
    | 'Revenue Growth %'
    | 'Advertising'
    | 'Postage'
    | 'Inventory'
    | 'Total Cost of Goods Sold'
    | 'Gross Profit'
    | 'Gross Profit Growth %'
    | 'Gross Margin'
    | 'Check Outflows'
    | 'Payroll and Consultants'
    | 'Insurance'
    | 'Utilities'
    | 'General Payment'
    | 'Software'
    | 'Refunds'
    | 'Rent'
    | 'Credit Card'
    | 'Other Expenses'
    | 'Travel'
    | 'Charges/Fees'
    | 'Overdraft/NSF Fees'
    | 'Total Operational Expenses'
    | 'EBITDA'
    | 'EBITDA Growth %'
    | 'EBITDA Margin'
    | 'Taxes'
    | 'Total Net Profit / Loss'
    | 'Company Investments'
    | 'Reconciled Intra-Company transfers'
    | 'Unreconciled Intra-Company transfers'
    | 'Debt Investment'
    | 'Debt Repayment'
    | 'Equity Investment'
    | 'Special Inflows'
    | 'Special Outflows'
    | 'P2P Transfers'
    | 'ATM Cash Outflows';

// Amount object used throughout the metrics
export interface HeronAmount {
    amount: string;
    currency: string;
}

// Financial metrics interface
export interface HeronFinancialMetrics {
    amount: HeronAmount;
    changed_in_last_90_days: boolean;
    count: number;
    duration_days: number;
    estimated_monthly_amount?: HeronAmount | null;
    frequency: string;
    is_active: boolean;
    max_amount?: HeronAmount | null;
    max_timestamp?: string | null;
    mean_amount?: HeronAmount | null;
    mean_interval_days?: string | null;
    median_amount?: HeronAmount | null;
    min_amount?: HeronAmount | null;
    min_timestamp?: string | null;
    mode_amount?: HeronAmount | null;
    mode_interval_days?: number | null;
    std_dev_amount?: HeronAmount | null;
}

// Merchant summary item interface
export interface HeronMerchantSummaryItem {
    group?: string | null;
    heron_id: string;
    inflow_metrics?: HeronFinancialMetrics | null;
    name?: string | null;
    net_metrics?: HeronFinancialMetrics | null;
    outflow_metrics?: HeronFinancialMetrics | null;
}

// Main response class
export class HeronMerchantSummaryResponse {
    by_merchant_summary: HeronMerchantSummaryItem[];
}

// Account balance interface
export interface HeronAccountBalance {
    account_id: string;
    account_info: any | null; // Can be expanded later if account_info structure is known
    balance: number;
    currency: string;
    is_computed: boolean;
    is_latest: boolean;
    timestamp: string | null;
}

// Daily balance interface
export interface HeronDailyBalance {
    accounts: HeronAccountBalance[];
    balance: number;
    currency: string;
    date: string; // YYYY-MM-DD format
}

// Main balances response interface
export interface HeronBalancesResponse {
    daily_balances: HeronDailyBalance[];
    forecasted: HeronDailyBalance[]; // Same structure as daily_balances
    summary: HeronDailyBalance;
}
